import { useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, ShieldCheck, Zap, Headset } from "lucide-react";

const TRUST = [
  { icon: ShieldCheck, title: "Secure & Reliable", desc: "Modern, dependable websites" },
  { icon: Zap, title: "Fast Turnaround", desc: "Built quickly and efficiently" },
  { icon: Headset, title: "Personal Support", desc: "Work directly with the founders" },
];

export const Hero = () => {
  const cardRef = useRef(null);
  const navigate = useNavigate();

  const onMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(1100px) rotateY(${px * 7}deg) rotateX(${-py * 7}deg)`;
  };
  const onLeave = () => {
    if (cardRef.current) cardRef.current.style.transform = "perspective(1100px) rotateY(0) rotateX(0)";
  };

  return (
    <section id="top" className="relative pt-28 md:pt-32 pb-6 px-5 md:px-10">
      <div className="max-w-[1440px] mx-auto">
        <div className="glass-panel relative overflow-hidden px-6 py-12 md:px-14 md:py-20">
          <div className="aurora-wave" aria-hidden="true" />
          <div className="reflect-light" aria-hidden="true" />

          <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="text-xs md:text-sm font-bold tracking-[0.22em] text-[#39D5FF] mb-5" data-testid="hero-eyebrow">
                PROFESSIONAL WEBSITE CREATION
              </motion.p>

              <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#F7FAFF]" data-testid="hero-heading">
                Smart AI Solutions.<br />
                <span className="text-gradient">Real Business Impact.</span>
              </motion.h1>

              <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
                className="mt-6 text-base md:text-lg text-[#AEBFD8] leading-relaxed max-w-xl">
                We build modern, professional websites that help businesses attract customers and get found online.
                You get a fast turnaround with the care and judgement of real people.
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
                className="mt-8 flex flex-col sm:flex-row gap-4">
                <button onClick={() => navigate("/contact")} data-testid="hero-primary-cta"
                  className="btn-primary inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-bold min-h-[44px]">
                  Get a Free Consultation <ArrowRight size={18} />
                </button>
                <button onClick={() => navigate("/portfolio")} data-testid="hero-secondary-cta"
                  className="btn-secondary inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-semibold min-h-[44px]">
                  <Play size={16} /> View Our Work
                </button>
              </motion.div>

              <div className="mt-10 grid sm:grid-cols-3 gap-4">
                {TRUST.map((t, i) => (
                  <motion.div key={t.title} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.25 + i * 0.08 }}
                    className="glass-card px-4 py-4 flex items-start gap-3" data-testid="hero-trust-indicator">
                    <span className="shrink-0 grid place-items-center w-9 h-9 rounded-xl bg-[rgba(0,168,255,0.15)] border border-[rgba(65,176,255,0.4)]">
                      <t.icon size={18} className="text-[#39D5FF]" />
                    </span>
                    <span>
                      <span className="block text-sm font-bold text-[#F7FAFF]">{t.title}</span>
                      <span className="block text-xs text-[#7F92AE] mt-0.5">{t.desc}</span>
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="flex justify-center lg:justify-end">
              <div ref={cardRef} onMouseMove={onMove} onMouseLeave={onLeave}
                className="glass-card relative w-full max-w-[460px] aspect-[4/3.4] flex flex-col items-center justify-center transition-transform duration-200 ease-out"
                style={{ borderColor: "rgba(65,176,255,0.65)" }} data-testid="hero-logo-card">
                <div className="absolute inset-0 rounded-[16px] overflow-hidden" aria-hidden="true">
                  <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-[radial-gradient(circle,rgba(0,168,255,0.45),transparent_70%)] blur-2xl" />
                </div>
                <img src="/assets/logo.png" alt="Harman AI Solutions symbol"
                  className="relative z-10 w-24 md:w-32 drop-shadow-[0_0_28px_rgba(0,168,255,0.6)]" />
                <div className="relative z-10 mt-6 text-center">
                  <span className="text-2xl md:text-3xl font-extrabold tracking-tight">
                    <span className="text-[#F7FAFF]">Harman </span>
                    <span className="text-gradient">AI</span>
                    <span className="text-[#AEBFD8] font-medium"> Solutions</span>
                  </span>
                </div>
                <div className="absolute inset-x-6 top-3 h-1/3 rounded-[14px] bg-gradient-to-b from-white/10 to-transparent pointer-events-none" aria-hidden="true" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
